import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getInvoiceFileRequest,
  type InvoiceFileType,
} from '@/features/deposit-bank-transfer/api';
import { CACHE_TIME_5_MINUTES } from '@/shared/config';
import { convertHtmlToPdf } from '@/shared/lib/htmlToPdf';
import { processHtmlLinks } from '@/shared/lib';

type PdfResult = {
  type: 'pdf';
  blob: Blob;
  blobUrl: string;
  fileName: string;
  contentType: string;
  size: number;
};

type HtmlResult = {
  type: 'html';
  html: string;
  fileName: string;
  contentType: string;
};

export type InvoiceFileResult = PdfResult | HtmlResult;

export type UseInvoiceDownloadParams = {
  invoiceId?: string;
  fileType: InvoiceFileType;
  enabled?: boolean;
};

function parseFileNameFromContentDisposition(
  header?: string | null
): string | undefined {
  if (!header) return undefined;
  // Examples: attachment; filename="Deposit_ABC.pdf"; filename*=UTF-8''Deposit_ABC.pdf
  const filenameStarMatch = header.match(/filename\*=([^']*)''([^;]+)/i);
  if (filenameStarMatch && filenameStarMatch[2]) {
    try {
      return decodeURIComponent(filenameStarMatch[2]);
    } catch {
      return filenameStarMatch[2];
    }
  }
  const filenameMatch = header.match(/filename="?([^";]+)"?/i);
  if (filenameMatch && filenameMatch[1]) {
    return filenameMatch[1];
  }
  return undefined;
}

async function fetchInvoiceFile(
  invoiceId: string,
  fileType: InvoiceFileType
): Promise<InvoiceFileResult> {
  const res = await getInvoiceFileRequest(invoiceId, fileType);
  const accept = fileType === 'pdf' ? 'application/pdf' : 'text/html';

  const contentDisposition =
    (res.headers &&
      (res.headers['content-disposition'] as string | undefined)) ||
    undefined;
  const contentType =
    (res.headers && (res.headers['content-type'] as string | undefined)) ||
    accept;

  const parsedName = parseFileNameFromContentDisposition(contentDisposition);

  if (fileType === 'pdf') {
    const blob = res.data as Blob;
    const blobUrl = URL.createObjectURL(blob);
    return {
      type: 'pdf',
      blob,
      blobUrl,
      contentType,
      fileName: parsedName || `Deposit_${invoiceId}.pdf`,
      size: blob.size,
    };
  }

  const html = processHtmlLinks(res.data as string);
  return {
    type: 'html',
    html,
    contentType,
    fileName: parsedName || `Deposit_${invoiceId}.html`,
  };
}

export function useInvoiceDownload(params: UseInvoiceDownloadParams) {
  const { invoiceId, fileType, enabled = true } = params;

  // Add state for PDF conversion
  const [isConvertingToPdf, setIsConvertingToPdf] = useState(false);
  const [conversionError, setConversionError] = useState<string | null>(null);
  const [convertedPdf, setConvertedPdf] = useState<{
    blob: Blob;
    size: number;
    blobUrl: string;
  } | null>(null);

  const query = useQuery<InvoiceFileResult>({
    queryKey: ['invoiceFile', invoiceId, fileType],
    queryFn: () => fetchInvoiceFile(invoiceId!, fileType),
    enabled: Boolean(invoiceId) && enabled,
    staleTime: 0,
    gcTime: CACHE_TIME_5_MINUTES,
    retry: 1,
  });

  // Convert HTML to PDF when HTML data is available
  useEffect(() => {
    const convertHtml = async () => {
      if (query.data?.type === 'html' && !convertedPdf) {
        setIsConvertingToPdf(true);
        setConversionError(null);

        try {
          const result = await convertHtmlToPdf(query.data.html);
          setConvertedPdf(result);
        } catch (error) {
          console.error('Error converting HTML to PDF:', error);
          setConversionError(
            error instanceof Error ? error.message : 'Conversion failed'
          );
        } finally {
          setIsConvertingToPdf(false);
        }
      }
    };

    convertHtml();
  }, [query.data, convertedPdf]);

  // Clean up blob URLs
  useEffect(() => {
    return () => {
      if (convertedPdf?.blobUrl) {
        URL.revokeObjectURL(convertedPdf.blobUrl);
      }
    };
  }, [convertedPdf]);

  const download = useCallback(
    (fileName?: string) => {
      const d = query.data;
      if (!d) return;

      if (d.type === 'pdf') {
        // Handle direct PDF download
        const link = document.createElement('a');
        link.href = d.blobUrl;
        link.download = fileName || d.fileName;
        link.click();
        return;
      }

      if (d.type === 'html' && convertedPdf) {
        // Download converted PDF
        const link = document.createElement('a');
        link.href = convertedPdf.blobUrl;
        link.download = fileName || 'invoice.pdf';
        link.click();
      }
    },
    [query.data, convertedPdf]
  );

  const print = useCallback(() => {
    const d = query.data;
    if (!d) return;

    if (d.type === 'pdf') {
      // Handle direct PDF print
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = d.blobUrl;
      document.body.appendChild(iframe);
      iframe.contentWindow?.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
      return;
    }

    if (d.type === 'html') {
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.style.position = 'absolute';
      iframe.style.left = '-9999px';
      iframe.style.top = '-9999px';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      document.body.appendChild(iframe);

      const iframeDoc =
        iframe.contentDocument || iframe.contentWindow?.document;

      if (!iframeDoc) {
        return;
      }

      iframeDoc.open();

      if (/<!DOCTYPE/i.test(d.html)) {
        iframeDoc.write(d.html);
      } else {
        iframeDoc.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Invoice</title>
            </head>
            <body>
              ${d.html}
            </body>
          </html>
        `);
      }

      iframeDoc.close();

      iframe.onload = () => {
        if (iframe.contentWindow) {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        }

        setTimeout(() => {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
        }, 1000);
      };
    }
  }, [query.data]);

  const getFileSize = useCallback(() => {
    const d = query.data;
    if (!d) return 0;

    if (d.type === 'pdf') {
      return d.size;
    }

    if (d.type === 'html' && convertedPdf) {
      return convertedPdf.size;
    }

    return 0;
  }, [query.data, convertedPdf]);

  return {
    ...query,
    download,
    print,
    getFileSize,
    isConvertingToPdf,
    conversionError,
  };
}
